import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Phone, 
  Droplets, 
  Zap, 
  CloudLightning, 
  Layers, 
  Wrench, 
  Building2,
  CheckCircle,
  ArrowRight
} from "lucide-react";

const images = {
  hero: "https://images.unsplash.com/photo-1681049400158-0ff6249ac315?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxyb29mZXIlMjByZXBhaXJpbmclMjByb29mJTIwc2FmZXR5JTIwZ2VhcnxlbnwwfHx8fDE3NzI3OTIwNjJ8MA&ixlib=rb-4.1.0&q=85",
  leak: "https://images.unsplash.com/photo-1689429028419-b143af3a4116?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxzdG9ybSUyMGRhbWFnZSUyMHJvb2YlMjBzaGluZ2xlcyUyMGxlYWt8ZW58MHx8fHwxNzcyNzkyMDc0fDA&ixlib=rb-4.1.0&q=85",
  residential: "https://images.unsplash.com/photo-1766392911915-b17454efe76e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvdXNlJTIwcm9vZiUyMGV4dGVyaW9yfGVufDB8fHx8MTc3Mjc5MjA2M3ww&ixlib=rb-4.1.0&q=85",
  commercial: "https://images.unsplash.com/photo-1603937124399-a530c64d5f0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwZmxhdCUyMHJvb2YlMjBidWlsZGluZyUyMHNreWxpbmV8ZW58MHx8fHwxNzcyNzkyMDc2fDA&ixlib=rb-4.1.0&q=85",
  worker: "https://images.unsplash.com/photo-1633759593085-1eaeb724fc88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxyb29mJTIwcmVwYWlyJTIwd29ya2VyJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzcyNzkyMTIxfDA&ixlib=rb-4.1.0&q=85",
  shingles: "https://images.unsplash.com/photo-1553169507-38833977274b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxyb29mJTIwcmVwYWlyJTIwd29ya2VyJTIwc2hpbmdsZXN8ZW58MHx8fHwxNzcyNzkyMTIxfDA&ixlib=rb-4.1.0&q=85",
};

const services = [
  {
    id: "roof-leak-repair",
    icon: Droplets,
    title: "Roof Leak Repair",
    shortDesc: "Fast identification and repair of leaks to prevent water damage.",
    image: images.leak,
    description: "Roof leaks can cause significant damage to your home if not addressed quickly. Our expert team uses advanced detection methods to locate the source of leaks and provide lasting repairs.",
    features: [
      "Advanced leak detection technology",
      "Same-day repair service available",
      "Interior damage assessment",
      "Preventive waterproofing solutions",
      "Written warranty on all repairs"
    ]
  },
  {
    id: "emergency-roof-repair",
    icon: Zap,
    title: "Emergency Roof Repair",
    shortDesc: "24/7 emergency response for urgent roof damage.",
    image: images.hero,
    description: "When disaster strikes, you need immediate help. Our emergency response team is available 24/7 to secure your property and prevent further damage from storms, fallen trees, or sudden failures.",
    features: [
      "24/7 emergency hotline",
      "60-minute response time",
      "Temporary protective tarping",
      "Insurance claim assistance",
      "Priority scheduling for repairs"
    ]
  },
  {
    id: "storm-damage-repair",
    icon: CloudLightning,
    title: "Storm Damage Repair",
    shortDesc: "Expert repair of wind, hail, and storm damage.",
    image: images.leak,
    description: "Los Angeles weather can be unpredictable. Our storm damage specialists assess and repair damage from wind, rain, and debris, working directly with your insurance company to streamline claims.",
    features: [
      "Comprehensive damage assessment",
      "Insurance documentation support",
      "Wind and hail damage repair",
      "Debris removal and cleanup",
      "Full restoration services"
    ]
  },
  {
    id: "roof-replacement",
    icon: Layers,
    title: "Roof Replacement",
    shortDesc: "Complete roof replacement with premium materials.",
    image: images.residential,
    description: "When repairs aren't enough, our full roof replacement service gives your home a fresh start. We use only premium materials and provide industry-leading warranties for your peace of mind.",
    features: [
      "Free detailed estimate",
      "Premium material options",
      "Complete tear-off and disposal",
      "Structural inspection included",
      "25-50 year material warranties"
    ]
  },
  {
    id: "shingle-repair",
    icon: Wrench,
    title: "Shingle Repair",
    shortDesc: "Quick repair and replacement of damaged shingles.",
    image: images.shingles,
    description: "Damaged, missing, or curling shingles can lead to bigger problems. Our shingle repair service addresses issues before they escalate, extending the life of your existing roof.",
    features: [
      "Color matching for seamless repairs",
      "Cracked and curling shingle repair",
      "Missing shingle replacement",
      "Flashing inspection and repair",
      "Gutter cleaning included"
    ]
  },
  {
    id: "commercial-roofing",
    icon: Building2,
    title: "Commercial Roofing",
    shortDesc: "Professional solutions for commercial properties.",
    image: images.commercial,
    description: "We understand that commercial roofing requires specialized expertise. Our commercial team handles flat roofs, metal roofing, and large-scale projects with minimal disruption to your business.",
    features: [
      "Flat roof specialists",
      "TPO and EPDM systems",
      "Metal roofing installation",
      "Minimal business disruption",
      "Maintenance programs available"
    ]
  }
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0F172A]">
        <div className="absolute inset-0 opacity-20">
          <img src={images.hero} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl animate-fadeInUp">
            <span className="text-[#F97316] font-semibold uppercase tracking-wider text-sm">Our Services</span>
            <h1 className="heading-h1 text-white mt-2 mb-6">PROFESSIONAL ROOFING SERVICES</h1>
            <p className="text-slate-300 text-lg mb-8">
              From emergency repairs to complete roof replacements, Cart Genix delivers 
              expert roofing solutions for residential and commercial properties throughout Los Angeles.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button className="btn-primary" data-testid="services-cta-quote">
                  Get Free Quote
                </Button>
              </Link>
              <a href="tel:+18884451244">
                <Button className="btn-secondary" data-testid="services-cta-call">
                  <Phone className="w-4 h-4" />
                  Call: 888-445-1244
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-gray">
        <div className="container-custom">
          <div className="grid gap-16">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`service-detail-${service.id}`}
              >
                <div className={`animate-fadeInUp ${index % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="image-hover">
                    <img 
                      src={service.image} 
                      alt={service.title} 
                      className="w-full h-80 object-cover rounded-lg shadow-xl"
                    />
                  </div>
                </div>
                
                <div className={`animate-fadeInUp delay-200 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="w-14 h-14 bg-orange-500/10 rounded-lg flex items-center justify-center text-[#F97316] mb-6">
                    <service.icon className="w-7 h-7" />
                  </div>
                  <h2 className="heading-h2 text-slate-900 mb-4">{service.title}</h2>
                  <p className="text-body text-lg mb-6">{service.description}</p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-[#F97316] flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link to="/contact">
                    <Button className="btn-primary">
                      Request Service <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#F97316]" data-testid="services-cta-section">
        <div className="container-custom text-center">
          <h2 className="heading-h2 text-white mb-4">NEED ROOFING HELP TODAY?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Don't wait for small problems to become big ones. Contact Cart Genix now for 
            a free inspection and estimate on any roofing service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-[#F97316] hover:bg-slate-100 font-bold h-12 px-8 rounded-md shadow-lg">
                Schedule Free Inspection
              </Button>
            </Link>
            <a href="tel:+18884451244">
              <Button className="bg-[#0F172A] hover:bg-slate-800 text-white font-bold h-12 px-8 rounded-md shadow-lg">
                <Phone className="w-4 h-4" />
                Call: 888-445-1244
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
